import { Clock } from 'lucide-react';
import { TextField, Button, Checkbox } from '@/interfaces/learner/ui';
import { SuggestionCard } from './SuggestionCard';
import type { LessonSuggestion } from '@formency/shared';

interface SuggestionPhaseProps {
  suggestions: LessonSuggestion[];
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  isFileFlow: boolean;
  copyrightConfirmed: boolean;
  onCopyrightChange: (confirmed: boolean) => void;
  refineFeedback: string;
  onRefineFeedbackChange: (feedback: string) => void;
  onRefine: (feedback: string) => void;
  isRefining: boolean;
}

// ─── Component ─────────────────────────────────────────────────────────────

export function SuggestionPhase({
  suggestions,
  selectedIndex,
  onSelect,
  isFileFlow,
  copyrightConfirmed,
  onCopyrightChange,
  refineFeedback,
  onRefineFeedbackChange,
  onRefine,
  isRefining,
}: SuggestionPhaseProps) {
  const isSingle = suggestions.length === 1;

  // ── Shared: copyright checkbox ───────────────────────────────────────────

  const showCopyright = isFileFlow && (isSingle || selectedIndex !== null);

  const copyrightCheckbox = showCopyright ? (
    <Checkbox isSelected={copyrightConfirmed} onChange={onCopyrightChange} size="sm">
      I have the right to use this material for learning purposes
    </Checkbox>
  ) : null;

  // ── Shared: refinement area ──────────────────────────────────────────────

  const refinementArea = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        borderTop: '1px solid var(--color-surface-border)',
        paddingTop: 12,
      }}
    >
      <p
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: 'var(--color-text-muted)',
          fontFamily: 'var(--font-sans)',
          margin: 0,
        }}
      >
        Looking for something else?
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <TextField
            aria-label="Refinement feedback"
            placeholder="Describe what you were hoping for..."
            value={refineFeedback}
            onChange={onRefineFeedbackChange}
            size="sm"
          />
        </div>
        <Button
          variant="secondary"
          size="sm"
          onPress={() => {
            if (refineFeedback.trim().length >= 3) onRefine(refineFeedback.trim());
          }}
          isDisabled={refineFeedback.trim().length < 3 || isRefining}
        >
          {isRefining ? 'Refining...' : 'Regenerate'}
        </Button>
      </div>
    </div>
  );

  // ── Single suggestion: confirmatory layout ───────────────────────────────

  if (isSingle) {
    const suggestion = suggestions[0];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <p
          style={{
            fontSize: 14,
            color: 'var(--color-text-secondary)',
            fontFamily: 'var(--font-sans)',
            margin: 0,
          }}
        >
          Here&rsquo;s what we&rsquo;ll cover:
        </p>

        {/* Inline suggestion display */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: '14px 16px',
            borderRadius: 12,
            border: '1px solid var(--color-surface-border)',
            background: 'var(--color-surface-solid)',
          }}
        >
          <p
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: 'var(--color-text)',
              margin: 0,
              lineHeight: 1.3,
              fontFamily: 'var(--font-sans)',
            }}
          >
            {suggestion.title}
          </p>
          <p
            style={{
              fontSize: 14,
              lineHeight: 1.6,
              color: 'var(--color-text-secondary)',
              margin: 0,
              fontFamily: 'var(--font-sans)',
            }}
          >
            {suggestion.description}
          </p>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 10,
              marginTop: 2,
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 12,
                color: 'var(--color-text-muted)',
                fontFamily: 'var(--font-sans)',
              }}
            >
              <Clock size={12} />~{suggestion.estimatedMinutes} min
            </span>
          </div>
        </div>

        {copyrightCheckbox}
        {refinementArea}
      </div>
    );
  }

  // ── Multiple suggestions: picker layout ──────────────────────────────────

  const headerText = isFileFlow
    ? `We found ${suggestions.length} lessons in your document:`
    : 'This is a broad topic \u2014 pick an angle:';

  const hasSelection = selectedIndex !== null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <p
        style={{
          fontSize: 14,
          color: 'var(--color-text-secondary)',
          fontFamily: 'var(--font-sans)',
          margin: 0,
        }}
      >
        {headerText}
      </p>

      {/* Suggestion cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {suggestions.map((s) => (
          <SuggestionCard
            key={s.index}
            suggestion={s}
            isSelected={selectedIndex === s.index}
            isOtherSelected={hasSelection && selectedIndex !== s.index}
            onSelect={() => onSelect(s.index)}
          />
        ))}
      </div>

      {copyrightCheckbox}
      {refinementArea}
    </div>
  );
}
